import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import ru from 'i18n-iso-countries/langs/ru.json';
import {TFunction} from 'i18next';

import {ELanguage} from '../constants';
import {getCurrentLang} from './getCurrentLang';

type TPriorityHash = Record<string, number>;

const priorityHashISO2: TPriorityHash = {
  RU: 1,
  // KZ: 2,
  // UA: 3,
};
const priorityHashISO3: TPriorityHash = {
  RUS: 1,
  // KAZ: 2,
  // UKR: 3,
};
const sortedOptions = {
  ignorePunctuation: true,
};

const isRuLang = getCurrentLang() === ELanguage.ru;

const alpha2ToAlpha3Extra: Record<string, string> = {
  AB: 'ABH',
  OS: 'OST',
};

const alpha3ToAlpha2Extra: Record<string, string> = {
  ABH: 'AB',
  OST: 'OS',
};

const ruCountries: Record<string, string | string[]> = {
  ...ru.countries,
  AB: 'Абхазия',
  OS: 'Южная Осетия',
  KG: 'Кыргызстан',
};

const enCountries: Record<string, string | string[]> = {
  ...en.countries,
  AB: 'Abkhazia',
  OS: 'South Ossetia',
};

const alpha2ToAlpha3 = (key: string) => {
  const alpha3 = countries.alpha2ToAlpha3(key);

  return alpha3 || alpha2ToAlpha3Extra[key];
};

const alpha3ToAlpha2 = (key: string) => {
  const alpha2 = countries.alpha3ToAlpha2(key);

  return alpha2 || alpha3ToAlpha2Extra[key];
};

const prepareCountryHash = (map: Record<string, string | string[]>, isISO3?: boolean) => {
  const result: Record<string, string> = {};

  Object.entries(map).forEach(([key, value]) => {
    const newKey = isISO3 ? alpha2ToAlpha3(key) : key;

    result[newKey] = Array.isArray(value) ? value[0] : value;
  });

  return result;
};

const sortByHashPriority = (hash: TPriorityHash) => (a: string, b: string) => {
  if (!hash[a] && !hash[b]) {
    return 0;
  }

  if (!hash[a]) {
    return 1;
  }

  return hash[b] ? hash[a] - hash[b] : -1;
};

const sortLocalList = (hash: Record<string, string>, local: string) =>
  Object.keys(hash).sort((a, b) => hash[a].localeCompare(hash[b], local, sortedOptions));

const priorityByLocal = (arr: string[], priority: TPriorityHash) =>
  arr.sort(sortByHashPriority(priority));

const ISO2RuCountryHash = prepareCountryHash(ruCountries);
const ISO2EnCountryHash = prepareCountryHash(enCountries);

const ISO2RuCountryCodeList = sortLocalList(ISO2RuCountryHash, 'ru');
const ISO2EnCountryCodeList = sortLocalList(ISO2EnCountryHash, 'en');

const ISO2CountryCodeList = isRuLang
  ? priorityByLocal(ISO2RuCountryCodeList, priorityHashISO2)
  : ISO2EnCountryCodeList;

const ISO3RuCountryHash = prepareCountryHash(ruCountries, true);
const ISO3EnCountryHash = prepareCountryHash(enCountries, true);

const ISO3RuCountryCodeList = sortLocalList(ISO3RuCountryHash, 'ru');
const ISO3EnCountryCodeList = sortLocalList(ISO3EnCountryHash, 'en');

const ISO3CountryCodeList = isRuLang
  ? priorityByLocal(ISO3RuCountryCodeList, priorityHashISO3)
  : ISO3EnCountryCodeList;

const getCountryLabel = (t?: TFunction, code?: string) =>
  t && code ? t([`Country:iso2.${code}`, `Country:iso3.${code}`, code]) : code;

const getCountryOptions = (t?: TFunction, useISO2?: boolean) => {
  const countryCodeList = useISO2 ? ISO2CountryCodeList : ISO3CountryCodeList;

  return countryCodeList.map((code) => ({
    label: getCountryLabel(t, code),
    value: code,
  }));
};

export {
  ISO2RuCountryHash,
  ISO2EnCountryHash,
  ISO3RuCountryHash,
  ISO3EnCountryHash,
  ISO2CountryCodeList,
  ISO3CountryCodeList,
  getCountryOptions,
  getCountryLabel,
  alpha3ToAlpha2,
};
